/**
 * Free-form text <-> numeric-array conversion for the common-cals editor.
 *
 * Parsing is forgiving: values may be separated by commas, whitespace,
 * newlines, tabs, or semicolons, and any surrounding brackets/braces/parens
 * are ignored — so a pasted array literal like `[1, 12, 3]` works as-is, as
 * does a bare `1 12 3` or a spreadsheet column. Serialization back to text is
 * delegated to the shared `formatValue` (calibration-params.ts) so the text
 * view matches the grid/diff precision.
 */

export interface ArrayTextStatus {
  values: number[];
  invalidTokens: string[];
  valid: boolean;
}

const TOKEN_SEPARATORS = /[\s,;()[\]{}]+/;

export function parseArrayText(text: string): {
  values: number[];
  invalidTokens: string[];
} {
  const values: number[] = [];
  const invalidTokens: string[] = [];
  for (const token of text.split(TOKEN_SEPARATORS)) {
    if (token.length === 0) continue;
    const n = Number(token);
    if (Number.isFinite(n)) values.push(n);
    else invalidTokens.push(token);
  }
  return { values, invalidTokens };
}

export function arrayTextStatus(text: string, elementCount: number): ArrayTextStatus {
  const { values, invalidTokens } = parseArrayText(text);
  return {
    values,
    invalidTokens,
    valid: invalidTokens.length === 0 && values.length === elementCount,
  };
}

/** Force an array to exactly `elementCount` entries (pad with 0, drop extras). */
export function toFixedLength(values: number[], elementCount: number): number[] {
  return Array.from({ length: elementCount }, (_, i) => values[i] ?? 0);
}
